import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import SiteHeader from './SiteHeader';
import { imageUrl } from '../config/images';
import { logInteraction } from '../utils/interactions';
import ProjectFinance from './ProjectFinance';
import {
  ArrowLeft,
  Calendar,
  Star,
  Clock,
  Tag,
  Users,
  Film,
  Play,
  Share,
  ChevronDown,
  ChevronUp,
  Tv,
  Zap,
  BookOpen
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// PROJECT_GREENLIT -> "Project Greenlit"; for the Insider development timeline.
const formatEventType = (t) => (t || '')
  .toLowerCase().split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
const formatEventDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '' : dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

// Map TMDB provider_id → URL template with {q} placeholder for title search
const PROVIDER_URLS = {
  8:    t => `https://www.netflix.com/search?q=${t}`,
  1796: t => `https://www.netflix.com/search?q=${t}`,   // Netflix with Ads
  9:    t => `https://www.amazon.com/s?k=${t}&i=instant-video`,
  10:   t => `https://www.amazon.com/s?k=${t}&i=instant-video`,
  119:  t => `https://www.amazon.com/s?k=${t}&i=instant-video`,  // Prime with Ads
  337:  t => `https://www.disneyplus.com/search/${t}`,
  384:  t => `https://www.max.com/search?q=${t}`,         // Max (HBO Max)
  1899: t => `https://www.max.com/search?q=${t}`,         // HBO Max
  78:   t => `https://www.max.com/search?q=${t}`,         // HBO
  1825: t => `https://www.amazon.com/s?k=${t}&i=instant-video`, // HBO Max Amazon Channel
  15:   t => `https://www.hulu.com/search?q=${t}`,
  531:  t => `https://www.paramountplus.com/search/${t}/`,
  2303: t => `https://www.paramountplus.com/search/${t}/`, // Paramount+ Premium
  2616: t => `https://www.paramountplus.com/search/${t}/`, // Paramount+ Essential
  582:  t => `https://www.amazon.com/s?k=${t}&i=instant-video`, // Paramount+ Amazon Channel
  633:  t => `https://www.paramountplus.com/search/${t}/`, // Paramount+ Roku Channel
  386:  t => `https://www.peacocktv.com/search?q=${t}`,
  387:  t => `https://www.peacocktv.com/search?q=${t}`,
  1770: t => `https://www.peacocktv.com/search?q=${t}`,  // Peacock Free
  350:  t => `https://tv.apple.com/search?term=${t}`,    // Apple TV+
  2:    t => `https://tv.apple.com/search?term=${t}`,    // Apple TV Store
  73:   t => `https://tubitv.com/search/${t}`,
  300:  t => `https://pluto.tv/search/details/${t}`,
  283:  t => `https://www.crunchyroll.com/search?q=${t}`,
  372:  t => `https://mubi.com/search?query=${t}`,
  43:   t => `https://www.starz.com/us/en/search?q=${t}`,
  37:   t => `https://www.sho.com/search#q=${t}`,
  695:  t => `https://www.amcplus.com/search/${t}`,
  39:   t => `https://www.nowtv.com/watch/search?q=${t}`,   // Now TV
  445:  t => `https://www.britbox.com/gb/search?q=${t}`,
  197:  t => `https://www.britbox.com/us/search?q=${t}`,   // BritBox Amazon Channel
  257:  t => `https://www.fubo.tv/search?q=${t}`,
  192:  t => `https://www.youtube.com/results?search_query=${t}`,
  188:  t => `https://www.youtube.com/results?search_query=${t}`,
  2528: t => `https://tv.youtube.com/search?q=${t}`,      // YouTube TV
  3:    t => `https://play.google.com/store/search?q=${t}&c=movies`,
  7:    t => `https://www.vudu.com/content/movies/search?searchString=${t}`,
  174:  t => `https://www.shudder.com/search?q=${t}`,
  142:  t => `https://www.discoveryplus.com/search?q=${t}`,
  538:  t => `https://watch.plex.tv/search?q=${t}`,         // Plex
  258:  t => `https://www.criterionchannel.com/search?q=${t}`, // Criterion Channel
  35:   t => `https://www.rakuten.tv/search?q=${t}`,        // Rakuten TV
  130:  t => `https://www.skystore.com/search?q=${t}`,      // Sky Store
  189:  t => `https://www.curzon.com/search/?q=${t}`,       // Curzon Home Cinema
  // UK / European providers
  29:   t => `https://www.sky.com/search?q=${t}`,           // Sky Go
  591:  t => `https://www.nowtv.com/watch/search?q=${t}`,   // Now TV Cinema
  41:   t => `https://www.itv.com/search?q=${t}`,           // ITVX (free with ads)
  2300: t => `https://www.itv.com/search?q=${t}`,           // ITVX Premium
  11:   t => `https://mubi.com/search?query=${t}`,          // MUBI (duplicate of 372 alias)
  201:  t => `https://mubi.com/search?query=${t}`,          // MUBI Amazon Channel
  224:  t => `https://player.bfi.org.uk/search?q=${t}`,    // BFI Player
  287:  t => `https://player.bfi.org.uk/search?q=${t}`,    // BFI Player Amazon Channel
  2041: t => `https://player.bfi.org.uk/search?q=${t}`,    // BFI Player Apple TV Channel
  1745: t => `https://www.curzon.com/search/?q=${t}`,      // Curzon Amazon Channel
  2100: t => `https://www.amazon.com/s?k=${t}&i=instant-video`, // Prime Video with Ads
};

const getProviderUrl = (provider, title) => {
  const t = encodeURIComponent(title || '');
  const fn = PROVIDER_URLS[provider.provider_id];
  if (fn) return fn(t);
  // Unmapped provider: send the user toward the actual service via search,
  // never to a third-party aggregator (TMDB/JustWatch) page.
  return `https://www.google.com/search?q=${encodeURIComponent(`watch ${title || ''} on ${provider.name || ''}`)}`;
};

// Sentinel for "every country at once", matching TMDBService.WORLDWIDE. Not a
// real ISO code, so it can never collide with a country in the picker.
const WORLDWIDE = 'WORLD';

const ProviderBadge = ({ provider, title }) => (
  <a
    href={getProviderUrl(provider, title)}
    target="_blank"
    rel="noopener noreferrer"
    title={
      provider.country_count
        ? `Watch on ${provider.name} — available in ${provider.country_count} countries: ${provider.countries.join(', ')}`
        : `Watch on ${provider.name}`
    }
    className="flex items-center gap-2 px-3 py-2 bg-videbits-50 border border-videbits-200 rounded-lg hover:bg-videbits-100 transition-colors"
  >
    {provider.logo ? (
      <img src={provider.logo} alt={provider.name} className="w-6 h-6 rounded object-cover" />
    ) : (
      <div className="w-6 h-6 rounded bg-gray-300 flex items-center justify-center text-xs font-bold text-gray-600">
        {provider.name?.[0]}
      </div>
    )}
    <span className="text-sm font-medium text-gray-800">{provider.name}</span>
    {/* Worldwide only: reach is the whole point of the aggregated view, and
        without it every provider looks equally available. */}
    {provider.country_count > 0 && (
      <span className="text-xs text-gray-500 tabular-nums">
        {provider.country_count}
      </span>
    )}
  </a>
);

const MovieDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [movie, setMovie] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [relatedMovies, setRelatedMovies] = useState([]);
  const [isInWatchlist, setIsInWatchlist] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showNotification, setShowNotification] = useState('');
  const [watchProviders, setWatchProviders] = useState(null);
  const [country, setCountry] = useState(localStorage.getItem('videbits_country') || '');
  const [regions, setRegions] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(1);
  const [seasonEpisodes, setSeasonEpisodes] = useState([]);
  const [loadingEpisodes, setLoadingEpisodes] = useState(false);
  const [expandedEpisodes, setExpandedEpisodes] = useState({});
  const [episodesOpen, setEpisodesOpen] = useState(false);
  const [showAllTags, setShowAllTags] = useState(false);
  const [insider, setInsider] = useState(null); // matching Insider project (same tmdb id)
  const [developmentOpen, setDevelopmentOpen] = useState(true); // collapsible Development section

  // API base URL - defaults to local development
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  useEffect(() => {
    if (id) {
      fetchMovieDetails();
      fetchRelatedMovies();
      checkUserActions();
    }
  }, [id]);

  // Log a title view once the title loads — feeds release-interest trends.
  useEffect(() => {
    if (movie) {
      const mediaType = window.location.pathname.includes('/tv/') ? 'tv' : 'movie';
      logInteraction('title_view', {
        tmdbId: movie.id || Number(id),
        mediaType,
        title: movie.title || movie.name,
      });
    }
  }, [movie, id]);

  // Link to the matching Insider project (same TMDb id) and surface its
  // development data (status, studios/producers, timeline).
  useEffect(() => {
    if (!movie) { setInsider(null); return; }
    const mediaType = window.location.pathname.includes('/tv/') ? 'tv' : 'movie';
    const tmdbId = movie.id || Number(id);
    let active = true;
    fetch(`${API_BASE}/api/insider/by-tmdb/${mediaType}/${tmdbId}`)
      .then((r) => (r.ok ? r.json() : { found: false }))
      .then((d) => { if (active) setInsider(d.found ? d : null); })
      .catch(() => {});
    return () => { active = false; };
  }, [movie, id]);

  // Check if user has already liked, bookmarked, or added to watchlist
  const checkUserActions = async () => {
    if (!isAuthenticated) {
      // For non-authenticated users, set defaults to false (no access to these features)
      setIsInWatchlist(false);
      setIsFavorited(false);
      setIsBookmarked(false);
      return;
    } else {
      // For authenticated users, check backend API
      try {
        const token = localStorage.getItem('videbits-token');
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        const [watchlistResponse, favoritesResponse] = await Promise.all([
          fetch(`${API_BASE}/watchlist/check/${id}`, { headers }),
          fetch(`${API_BASE}/favorites/check/${id}`, { headers })
        ]);

        if (watchlistResponse.ok) {
          const watchlistData = await watchlistResponse.json();
          setIsInWatchlist(watchlistData.inWatchlist);
        }

        if (favoritesResponse.ok) {
          const favoritesData = await favoritesResponse.json();
          setIsFavorited(favoritesData.isFavorited);
        }
      } catch (error) {
        console.error('Error checking user actions:', error);
      }
    }
  };

  const fetchMovieDetails = async () => {
    try {
      setLoading(true);
      setError('');

      // Detect if we're on a TV route or movie route
      const currentPath = window.location.pathname;
      const isTvRoute = currentPath.includes('/tv/');
      const isMovieRoute = currentPath.includes('/movie/');

      // If we have the summary from a search result / carousel, paint it
      // immediately for a fast first render — but still fetch the full record
      // below so TV seasons/episodes, cast and full overview load (the summary
      // lacks e.g. number_of_seasons). Don't return early.
      const storedMovie = sessionStorage.getItem(`movie_${id}`);
      if (storedMovie) {
        try {
          setMovie(JSON.parse(storedMovie));
        } catch (e) {
          console.log('Could not parse stored movie data');
        }
      }

      // Try to fetch from the appropriate endpoint based on route
      let response;
      let data;

      if (isTvRoute) {
        // We're on /tv/:id - try TV endpoint first
        response = await fetch(`${API_BASE}/tv/${id}`);
        if (response.ok) {
          data = await response.json();
          if (data && data.id) {
            setMovie(data);
            return;
          }
        }
        // If TV fails, try movie endpoint and redirect if found
        response = await fetch(`${API_BASE}/movies/tmdb/${id}`);
        if (response.ok) {
          data = await response.json();
          if (data && data.id) {
            console.log('ID is actually a movie, redirecting...');
            setMovie(data);
            navigate(`/movie/${id}`, { replace: true });
            return;
          }
        }
      } else if (isMovieRoute) {
        // We're on /movie/:id - try movie endpoint first
        response = await fetch(`${API_BASE}/movies/tmdb/${id}`);
        if (response.ok) {
          data = await response.json();
          if (data && data.id) {
            setMovie(data);
            return;
          }
        }
        // If movie fails, try TV endpoint and redirect if found
        response = await fetch(`${API_BASE}/tv/${id}`);
        if (response.ok) {
          data = await response.json();
          if (data && data.id) {
            console.log('ID is actually a TV series, redirecting...');
            setMovie(data);
            navigate(`/tv/${id}`, { replace: true });
            return;
          }
        }
      }

      // If both endpoints fail, show error
      throw new Error('Content not found');
    } catch (err) {
      console.error('Error fetching movie details:', err);
      // Keep the cached summary (if any) rather than blanking the page.
      if (!sessionStorage.getItem(`movie_${id}`)) {
        setError('Failed to load content details. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedMovies = async () => {
    if (!movie || !id) return;

    try {
      // Use TMDB similar movies API only
      // The ID from the URL is the TMDB ID
      const mediaType = movie?.category === 'TV Series' ? 'tv' : 'movie';
      const response = await fetch(`${API_BASE}/movies/${id}/related?limit=5&media_type=${mediaType}`);

      if (response.ok) {
        const data = await response.json();
        const related = data.results || [];
        setRelatedMovies(related);
        console.log(`Related movies from TMDB: ${related.length} results`);
      } else {
        console.warn('TMDB related movies endpoint returned no results');
        setRelatedMovies([]);
      }
    } catch (err) {
      console.error('Error fetching related movies from TMDB:', err);
      setRelatedMovies([]);
    }
  };

  const fetchWatchProviders = async (tmdbId, mediaType, countryCode) => {
    if (!tmdbId || !countryCode) return;
    try {
      const mt = mediaType === 'TV Series' ? 'tv' : 'movie';
      const response = await fetch(`${API_BASE}/watch-providers/${mt}/${tmdbId}?country=${countryCode}`);
      if (response.ok) {
        setWatchProviders(await response.json());
      }
    } catch (err) {
      console.error('Error fetching watch providers:', err);
    }
  };

  const detectCountry = async () => {
    const saved = localStorage.getItem('videbits_country');
    if (saved) return saved;
    try {
      const res = await fetch('https://ipapi.co/country/');
      if (res.ok) {
        const code = (await res.text()).trim();
        if (/^[A-Z]{2}$/.test(code)) return code;
      }
    } catch (_) {}
    return 'US';
  };

  // Fetch regions list once on mount
  useEffect(() => {
    fetch(`${API_BASE}/watch-providers/regions`)
      .then(r => r.ok ? r.json() : [])
      .then(setRegions)
      .catch(() => {});
  }, [API_BASE]);

  // Update related movies and watch providers when movie data changes
  useEffect(() => {
    if (!movie) return;
    fetchRelatedMovies();
    const resolveAndFetch = async () => {
      let resolved = country;
      if (!resolved) {
        resolved = await detectCountry();
        setCountry(resolved);
      }
      fetchWatchProviders(movie.tmdb_id, movie.category, resolved);
    };
    resolveAndFetch();
  }, [movie]);

  // Re-fetch providers when country changes (after initial load)
  useEffect(() => {
    if (movie?.tmdb_id && country) {
      fetchWatchProviders(movie.tmdb_id, movie.category, country);
    }
  }, [country]);

  useEffect(() => {
    if (movie?.category !== 'TV Series' || !movie?.number_of_seasons) return;
    const tmdbId = movie.tmdb_id || id;
    setLoadingEpisodes(true);
    setSeasonEpisodes([]);
    setExpandedEpisodes({});
    fetch(`${API_BASE}/tv/${tmdbId}/season/${selectedSeason}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => setSeasonEpisodes(data?.episodes || []))
      .catch(() => setSeasonEpisodes([]))
      .finally(() => setLoadingEpisodes(false));
  }, [movie, selectedSeason, id, API_BASE]);

  const toggleEpisode = (epNum) => {
    setExpandedEpisodes(prev => ({ ...prev, [epNum]: !prev[epNum] }));
  };

  const formatRating = (rating) => {
    if (!rating || rating === 0) return 'Not rated';
    return `${rating.toFixed(1)}/10`;
  };

  const formatRuntime = (runtime) => {
    if (!runtime) return 'Unknown';
    const hours = Math.floor(runtime / 60);
    const minutes = runtime % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const getRuntimeFromContent = (content) => {
    const match = content?.match(/Runtime:\s*(\d+)\s*minutes?/i);
    return match ? parseInt(match[1]) : null;
  };

  // Show notification helper
  const showNotificationMessage = (message) => {
    setShowNotification(message);
    setTimeout(() => setShowNotification(''), 3000);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: movie.title,
          text: movie.content?.substring(0, 200) + '...',
          url: window.location.href,
        });
        showNotificationMessage('Shared successfully!');
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      // Fallback: copy URL to clipboard
      try {
        await navigator.clipboard.writeText(window.location.href);
        showNotificationMessage('Link copied to clipboard!');
      } catch (err) {
        console.error('Could not copy link:', err);
        showNotificationMessage('Could not copy link');
      }
    }
  };

  const handleWatchlist = async () => {
    if (!isAuthenticated) {
      // For non-authenticated users, show login prompt
      showNotificationMessage('Please log in to use the watchlist feature');
      return;
    } else {
      // For authenticated users, use backend API
      try {
        const token = localStorage.getItem('videbits-token');
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        if (isInWatchlist) {
          // Remove from watchlist
          const response = await fetch(`${API_BASE}/watchlist/${movie.id}`, {
            method: 'DELETE',
            headers
          });
          
          if (response.ok) {
            setIsInWatchlist(false);
            showNotificationMessage('Removed from watchlist');
          } else {
            throw new Error('Failed to remove from watchlist');
          }
        } else {
          // Add to watchlist
          const movieData = {
            id: movie.id,
            title: movie.title,
            poster_path: movie.poster_path,
            release_date: movie.release_date,
            vote_average: movie.vote_average,
            overview: movie.overview,
            tmdb_id: movie.tmdb_id || movie.id,
            media_type: window.location.pathname.includes('/tv/') ? 'tv' : 'movie'
          };

          const response = await fetch(`${API_BASE}/watchlist`, {
            method: 'POST',
            headers,
            body: JSON.stringify(movieData)
          });

          if (response.ok) {
            setIsInWatchlist(true);
            showNotificationMessage('Added to watchlist!');
            const mediaType = window.location.pathname.includes('/tv/') ? 'tv' : 'movie';
            logInteraction('watchlist_add', { tmdbId: movie.id, mediaType, title: movie.title || movie.name });
          } else {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to add to watchlist');
          }
        }
      } catch (error) {
        console.error('Error updating watchlist:', error);
        showNotificationMessage(`Error updating watchlist: ${error.message}`);
      }
    }
  };

  const handleLike = async () => {
    if (!isAuthenticated) {
      // For non-authenticated users, show login prompt
      showNotificationMessage('Please log in to use the favorites feature');
      return;
    } else {
      // For authenticated users, use backend API
      try {
        const token = localStorage.getItem('videbits-token');
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        if (isFavorited) {
          // Remove from favorites
          const response = await fetch(`${API_BASE}/favorites/${movie.id}`, {
            method: 'DELETE',
            headers
          });
          
          if (response.ok) {
            setIsFavorited(false);
            showNotificationMessage('Removed from favorites');
          } else {
            throw new Error('Failed to remove from favorites');
          }
        } else {
          // Add to favorites
          const movieData = {
            id: movie.id,
            title: movie.title,
            poster_path: movie.poster_path,
            release_date: movie.release_date,
            vote_average: movie.vote_average,
            overview: movie.overview,
            tmdb_id: movie.tmdb_id || movie.id
          };
          
          const response = await fetch(`${API_BASE}/favorites`, {
            method: 'POST',
            headers,
            body: JSON.stringify(movieData)
          });
          
          if (response.ok) {
            setIsFavorited(true);
            showNotificationMessage('Added to favorites!');
          } else {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to add to favorites');
          }
        }
      } catch (error) {
        console.error('Error updating favorites:', error);
        showNotificationMessage(`Error updating favorites: ${error.message}`);
      }
    }
  };

  const handleBookmark = () => {
    if (!isAuthenticated) {
      // For non-authenticated users, show login prompt
      showNotificationMessage('Please log in to use the bookmark feature');
      return;
    }
    
    try {
      const bookmarks = JSON.parse(localStorage.getItem('videbits-bookmarks') || '[]');
      
      if (isBookmarked) {
        // Remove from bookmarks
        const updatedBookmarks = bookmarks.filter(item => item.id.toString() !== id.toString());
        localStorage.setItem('videbits-bookmarks', JSON.stringify(updatedBookmarks));
        setIsBookmarked(false);
        showNotificationMessage('Bookmark removed');
      } else {
        // Add to bookmarks
        const movieData = {
          id: movie.id,
          title: movie.title,
          poster_path: movie.poster_path,
          category: movie.category,
          vote_average: movie.vote_average,
          release_date: movie.release_date,
          addedAt: new Date().toISOString()
        };
        bookmarks.push(movieData);
        localStorage.setItem('videbits-bookmarks', JSON.stringify(bookmarks));
        setIsBookmarked(true);
        showNotificationMessage('Bookmarked!');
      }
    } catch (error) {
      console.error('Error updating bookmarks:', error);
      showNotificationMessage('Error updating bookmarks');
    }
  };

  const handleWatchTrailer = () => {
    // Search for trailer on YouTube
    const searchQuery = `${movie.title} ${movie.release_date ? new Date(movie.release_date).getFullYear() : ''} trailer`;
    const youtubeUrl = `https://www.youtube.com/results?search_query=${encodeURIComponent(searchQuery)}`;
    window.open(youtubeUrl, '_blank');
    showNotificationMessage('Searching for trailer...');
  };

  const handleTagClick = (tag) => {
    // For genre tags, create natural language queries that work with smart parsing
    const genreWords = ['action', 'horror', 'comedy', 'drama', 'thriller', 'sci-fi', 'science-fiction', 'animation', 'adventure', 'crime', 'fantasy'];
    
    let tagQuery;
    if (genreWords.includes(tag.toLowerCase())) {
      // For genre tags, use natural language that triggers genre search
      tagQuery = `${tag} movies`;
    } else {
      // For other tags (actors, directors, years, etc.), search directly
      tagQuery = tag;
    }
    
    // Navigate to home with search query and tag_search flag
    navigate(`/?q=${encodeURIComponent(tagQuery)}&tag_search=true`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-videbits-50 via-pink-50 to-videbits-50">
        <SiteHeader />
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-videbits-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !movie) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-videbits-50 via-pink-50 to-videbits-50">
        <SiteHeader />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <Link
              to="/"
              className="inline-flex items-center text-videbits-600 hover:text-videbits-800 mb-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Search
            </Link>
            <div className="text-red-600 mb-4">{error || 'Movie not found'}</div>
            <Link 
              to="/"
              className="text-videbits-600 hover:text-videbits-800 underline"
            >
              Go to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const runtime = getRuntimeFromContent(movie.content);

  // Cinema signal for a movie (not TV), derived from its release date (TMDB has
  // no showtimes). Two states: currently in its theatrical window (released in
  // the last ~90 days) -> "In Cinemas"; a near-future release (within ~120 days)
  // -> "Coming to Cinemas" with the date. Link out to Google showtimes.
  const cinema = (() => {
    if (window.location.pathname.includes('/tv/')) return null;
    if (!movie.release_date) return null;
    const rel = new Date(movie.release_date);
    if (isNaN(rel.getTime())) return null;
    const days = (Date.now() - rel.getTime()) / 86400000; // >0 = released X days ago
    const dateLabel = rel.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    if (days >= 0 && days <= 90) return { upcoming: false, dateLabel };
    if (days < 0 && days >= -120) return { upcoming: true, dateLabel };
    return null;
  })();
  const showtimesUrl =
    `https://www.google.com/search?q=${encodeURIComponent((movie.title || '') + ' showtimes')}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-videbits-50 via-pink-50 to-videbits-50">
      <SiteHeader />
      {/* Notification Toast */}
      {showNotification && (
        <div className="fixed top-4 right-4 bg-videbits-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 animate-pulse">
          {showNotification}
        </div>
      )}
      
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center text-videbits-600 hover:text-videbits-800 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Search
          </Link>
        </div>

        {/* Movie Details */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
          <div className="lg:flex">
            {/* Poster Section — keep the poster's natural 2:3 ratio (no stretch);
                self-start so it doesn't get stretched to the row's full height.
                On mobile, cap + centre it so a full-bleed 2:3 poster doesn't push
                all the info far below the fold. */}
            <div className="w-full max-w-[240px] mx-auto lg:max-w-none lg:mx-0 lg:w-1/3 bg-gray-100 lg:self-start">
              {movie.poster_path ? (
                <img
                  src={movie.poster_path}
                  alt={movie.title}
                  className="w-full object-cover"
                  style={{ aspectRatio: '2 / 3' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div
                className={`w-full bg-gradient-to-br from-videbits-200 to-videbits-300 ${movie.poster_path ? 'hidden' : 'flex'} items-center justify-center`}
                style={{ aspectRatio: '2 / 3' }}
              >
                <Film className="w-16 h-16 text-videbits-500" />
              </div>
            </div>

            {/* Content Section */}
            <div className="lg:w-2/3 p-8">
              <div className="flex justify-between items-start mb-6">
                <div className="flex-1">
                  <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                    {movie.title}
                  </h1>
                  
                  {/* Metadata Row */}
                  <div className="flex flex-wrap items-center text-sm text-gray-600 space-x-6 mb-4">
                    {movie.release_date && (
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(movie.release_date).getFullYear()}</span>
                      </div>
                    )}
                    
                    {runtime && (
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>{formatRuntime(runtime)}</span>
                      </div>
                    )}
                    
                    {movie.vote_average > 0 && (
                      <div className="flex items-center space-x-1">
                        <Star className="w-4 h-4 fill-videbits-400 text-videbits-400" />
                        <span>{formatRating(movie.vote_average)}</span>
                        {movie.vote_count && (
                          <span className="text-gray-400">({movie.vote_count} votes)</span>
                        )}
                      </div>
                    )}

                    {movie.category && (
                      <div className="flex items-center space-x-1">
                        <Tag className="w-4 h-4" />
                        <span className="px-2 py-1 bg-videbits-100 text-videbits-800 rounded-full text-xs">
                          {movie.category}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-2 ml-4">
                  <button 
                    onClick={handleShare}
                    className="p-2 bg-videbits-100 hover:bg-videbits-200 rounded-full transition-colors"
                    title="Share"
                  >
                    <Share className="w-5 h-5 text-videbits-700" />
                  </button>
                </div>
              </div>

              {/* Description */}
              {movie.content && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Overview</h3>
                  <p className="text-gray-700 leading-relaxed">{movie.content}</p>
                </div>
              )}

              {/* Based on (source material) — each author links to their writer
                  profile (person page), which aggregates every adaptation of
                  their work: insider intelligence on the original creator. */}
              {movie.based_on && movie.based_on.length > 0 && (() => {
                const byType = {};
                movie.based_on.forEach((b) => {
                  (byType[b.work_type] = byType[b.work_type] || []).push(b);
                });
                const groups = Object.entries(byType);
                return (
                  <div className="mb-6">
                    <p className="inline-flex items-start gap-2 text-sm text-gray-600 bg-videbits-50 border border-videbits-100 rounded-lg px-3 py-2">
                      <BookOpen className="w-4 h-4 text-videbits-600 mt-0.5 flex-shrink-0" />
                      <span>
                        Based on{' '}
                        {groups.map(([type, authors], gi) => (
                          <React.Fragment key={type}>
                            the {type} by{' '}
                            {authors.map((a, ai) => (
                              <React.Fragment key={a.author_id || a.author}>
                                {ai > 0 && (ai === authors.length - 1 ? ' and ' : ', ')}
                                {a.author_id ? (
                                  <Link
                                    to={`/person/${a.author_id}`}
                                    className="font-medium text-videbits-700 hover:text-videbits-900 hover:underline"
                                  >
                                    {a.author}
                                  </Link>
                                ) : (
                                  <span className="font-medium text-gray-800">{a.author}</span>
                                )}
                              </React.Fragment>
                            ))}
                            {gi < groups.length - 1 ? '; ' : ''}
                          </React.Fragment>
                        ))}
                      </span>
                    </p>
                  </div>
                );
              })()}

              {/* Tags */}
              {movie.tags && movie.tags.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {(showAllTags ? movie.tags : movie.tags.slice(0, 10)).map((tag, index) => (
                      <button
                        key={index}
                        onClick={() => handleTagClick(tag)}
                        className="px-3 py-1 bg-videbits-100 text-videbits-800 text-sm rounded-full hover:bg-videbits-200 transition-colors cursor-pointer border-none"
                        title={`Search for more "${tag}" content`}
                      >
                        {tag}
                      </button>
                    ))}
                    {movie.tags.length > 10 && (
                      <button
                        type="button"
                        onClick={() => setShowAllTags((v) => !v)}
                        className="px-3 py-1 bg-videbits-100 text-videbits-700 text-sm rounded-full hover:bg-videbits-200 transition-colors cursor-pointer border-none"
                      >
                        {showAllTags ? 'Show less' : `+${movie.tags.length - 10} more`}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {insider?.finance && <div className="mb-6"><ProjectFinance finance={insider.finance} /></div>}

              {/* VideBits Insider — development data merged onto the title page */}
              {insider && (insider.companies?.length > 0 || insider.events?.length > 0) && (
                <div className="mb-6 rounded-xl border border-videbits-200 bg-videbits-50 p-4">
                  <button
                    onClick={() => setDevelopmentOpen((o) => !o)}
                    className="w-full flex items-center justify-between gap-2 bg-transparent border-none cursor-pointer p-0"
                  >
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-videbits-500" />
                      <h3 className="text-sm font-semibold text-videbits-800">Development</h3>
                      {insider.status && (
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-videbits-100 text-videbits-700">{insider.status}</span>
                      )}
                    </div>
                    {developmentOpen ? <ChevronUp className="w-4 h-4 text-videbits-500" /> : <ChevronDown className="w-4 h-4 text-videbits-500" />}
                  </button>
                  {developmentOpen && (
                    <div className="mt-3">
                      {insider.companies && insider.companies.length > 0 && (
                        <p className="text-sm text-gray-700 mb-3">
                          <span className="text-gray-500">Studios &amp; producers: </span>
                          {insider.companies.map((c, i) => (
                            <span key={c.id}>
                              <Link to={`/company/${c.id}`} className="text-videbits-700 hover:underline">{c.name}</Link>
                              {i < insider.companies.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </p>
                      )}
                      {insider.events && insider.events.length > 0 && (
                        <ol className="relative border-l border-videbits-200 ml-1.5">
                          {insider.events.map((e, i) => (
                            <li key={i} className="mb-3 ml-4 last:mb-0">
                              <div className="absolute w-2 h-2 bg-videbits-300 rounded-full -left-1 mt-1.5" />
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-medium text-gray-900">{formatEventType(e.event_type)}</span>
                                {e.event_date && <span className="text-xs text-gray-400">{formatEventDate(e.event_date)}</span>}
                              </div>
                              {e.description && <p className="text-xs text-gray-600 mt-0.5">{e.description}</p>}
                            </li>
                          ))}
                        </ol>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={handleWatchTrailer}
                  className="flex items-center space-x-2 bg-videbits-600 text-white px-6 py-3 rounded-lg hover:bg-videbits-700 transition-colors"
                >
                  <Play className="w-5 h-5" />
                  <span>Watch Trailer</span>
                </button>
                <button
                  onClick={handleWatchlist}
                  disabled={!isAuthenticated}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-colors ${
                    !isAuthenticated
                      ? 'bg-gray-100 text-gray-500 opacity-50 cursor-not-allowed'
                      : isInWatchlist
                      ? 'bg-videbits-100 text-videbits-700 hover:bg-videbits-200'
                      : 'bg-videbits-100 text-videbits-700 hover:bg-videbits-200'
                  }`}
                  title={!isAuthenticated ? "Login required to use watchlist" : undefined}
                >
                  <Users className="w-5 h-5" />
                  <span>{!isAuthenticated ? 'Login to Use Watchlist' : (isInWatchlist ? 'In Watchlist' : 'Add to Watchlist')}</span>
                </button>
                <Link
                  to={`${movie.category === 'TV Series' ? '/tv' : '/movie'}/${id}/cast-crew`}
                  className="flex items-center space-x-2 bg-videbits-100 text-videbits-700 px-6 py-3 rounded-lg hover:bg-videbits-200 transition-colors"
                >
                  <Users className="w-5 h-5" />
                  <span>View Cast & Crew</span>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Episodes — TV series only */}
        {movie.category === 'TV Series' && movie.number_of_seasons > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            <button
              onClick={() => setEpisodesOpen(o => !o)}
              className="w-full flex items-center justify-between bg-transparent border-none cursor-pointer p-0 mb-6 group"
            >
              <div className="flex items-center gap-3">
                <Tv className="w-6 h-6 text-videbits-600" />
                <h2 className="text-2xl font-bold text-gray-900 group-hover:text-videbits-700 transition-colors">Episodes</h2>
                {movie.number_of_episodes > 0 && (
                  <span className="text-sm text-gray-500">{movie.number_of_episodes} episodes</span>
                )}
              </div>
              {episodesOpen ? <ChevronUp className="w-5 h-5 text-videbits-500" /> : <ChevronDown className="w-5 h-5 text-videbits-500" />}
            </button>

            {episodesOpen && (
            <>
            {/* Season tabs */}
            <div className="flex flex-wrap gap-2 mb-6">
              {Array.from({ length: movie.number_of_seasons }, (_, i) => i + 1).map(n => {
                const epCount = (movie.seasons || []).find(s => s.season_number === n)?.episode_count;
                return (
                <button
                  key={n}
                  onClick={() => setSelectedSeason(n)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border-none cursor-pointer ${
                    selectedSeason === n
                      ? 'bg-videbits-600 text-white'
                      : 'bg-videbits-50 text-videbits-700 hover:bg-videbits-100 hover:text-videbits-800'
                  }`}
                >
                  Season {n}{epCount ? ` · ${epCount} ep${epCount === 1 ? '' : 's'}` : ''}
                </button>
                );
              })}
            </div>

            {/* Episode list */}
            {loadingEpisodes ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-videbits-600"></div>
              </div>
            ) : seasonEpisodes.length === 0 ? (
              <p className="text-gray-500 text-sm">No episode data available.</p>
            ) : (
              <div className="divide-y divide-gray-100">
                {seasonEpisodes.map(ep => (
                  <div key={ep.episode_number} className="py-4">
                    <button
                      onClick={() => toggleEpisode(ep.episode_number)}
                      className="w-full text-left flex items-start justify-between gap-4 group bg-transparent border-none cursor-pointer p-0"
                    >
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        {/* Episode still image */}
                        {ep.still_path ? (
                          <img
                            src={imageUrl(ep.still_path, 'w300')}
                            alt={ep.name}
                            className="w-24 h-14 object-cover rounded-lg flex-shrink-0 bg-gray-100"
                          />
                        ) : (
                          <div className="w-24 h-14 rounded-lg flex-shrink-0 bg-videbits-50 flex items-center justify-center">
                            <Tv className="w-6 h-6 text-videbits-300" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-semibold text-videbits-600 bg-videbits-50 px-2 py-0.5 rounded">
                              E{String(ep.episode_number).padStart(2, '0')}
                            </span>
                            <h4 className="font-semibold text-gray-900 text-sm group-hover:text-videbits-700 transition-colors">
                              {ep.name}
                            </h4>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
                            {ep.air_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(ep.air_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </span>
                            )}
                            {ep.runtime > 0 && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {ep.runtime}m
                              </span>
                            )}
                            {ep.vote_average > 0 && (
                              <span className="flex items-center gap-1">
                                <Star className="w-3 h-3 fill-videbits-400 text-videbits-400" />
                                {ep.vote_average.toFixed(1)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex-shrink-0 mt-1 text-gray-400">
                        {expandedEpisodes[ep.episode_number]
                          ? <ChevronUp className="w-4 h-4" />
                          : <ChevronDown className="w-4 h-4" />}
                      </div>
                    </button>
                    {expandedEpisodes[ep.episode_number] && ep.overview && (
                      <p className="mt-3 ml-28 text-sm text-gray-600 leading-relaxed">
                        {ep.overview}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
            </>
            )}
          </div>
        )}

        {/* Where to Watch */}
        {watchProviders && (
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            {/* Header row */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-1">
              <h2 className="text-2xl font-bold text-gray-900">Where to Watch</h2>
              <div className="flex items-center gap-2">
                <label htmlFor="country-select" className="text-sm text-gray-500">Region:</label>
                <select
                  id="country-select"
                  value={country}
                  onChange={e => {
                    const code = e.target.value;
                    setCountry(code);
                    localStorage.setItem('videbits_country', code);
                  }}
                  className="text-sm border border-gray-200 rounded-lg px-2 py-1 bg-gray-50 text-gray-800 focus:outline-none focus:ring-2 focus:ring-videbits-400"
                >
                  {/* Worldwide first: it always works, where a country only
                      shows something if the title is licensed there. */}
                  <option value={WORLDWIDE}>🌍 Worldwide</option>
                  {regions.length > 0
                    ? regions.map(r => <option key={r.code} value={r.code}>{r.name}</option>)
                    : country !== WORLDWIDE && <option value={country}>{country}</option>
                  }
                </select>
              </div>
            </div>
            <p className="text-sm text-gray-400 mb-6">Data provided by JustWatch</p>

            {/* Cinemas (theatrical window) — shown above streaming options */}
            {cinema && (
              <div className="mb-5">
                <h3 className="text-sm font-semibold text-purple-700 uppercase tracking-wide mb-3 flex items-center gap-1">
                  <span className="inline-block w-2 h-2 rounded-full bg-purple-500"></span>
                  {cinema.upcoming ? 'Coming to Cinemas' : 'In Cinemas'}
                </h3>
                <div className="flex flex-wrap items-center gap-3">
                  {cinema.upcoming && (
                    <span className="text-sm text-gray-700 font-medium">{cinema.dateLabel}</span>
                  )}
                  <a
                    href={showtimesUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-lg border border-purple-200 hover:bg-purple-100 transition-colors text-sm font-medium"
                  >
                    <Film className="w-4 h-4" />
                    Find showtimes near you
                  </a>
                </div>
              </div>
            )}

            {(watchProviders.free.length === 0 && watchProviders.ads.length === 0 && watchProviders.flatrate.length === 0) ? (
              <p className="text-gray-500 text-sm">
                {cinema
                  ? 'Not yet available to stream.'
                  : country === WORLDWIDE
                    ? 'No streaming options found anywhere.'
                    : 'No streaming options found for this country. Try Worldwide.'}
              </p>
            ) : (
              <>
                {/* Free */}
                {watchProviders.free.length > 0 && (
                  <div className="mb-5">
                    <h3 className="text-sm font-semibold text-green-700 uppercase tracking-wide mb-3 flex items-center gap-1">
                      <span className="inline-block w-2 h-2 rounded-full bg-green-500"></span>
                      Free
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      {watchProviders.free.map(p => (
                        <ProviderBadge key={p.provider_id} provider={p} title={movie.title} fallback={watchProviders.link} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Free with Ads */}
                {watchProviders.ads.length > 0 && (
                  <div className="mb-5">
                    <h3 className="text-sm font-semibold text-blue-700 uppercase tracking-wide mb-3 flex items-center gap-1">
                      <span className="inline-block w-2 h-2 rounded-full bg-blue-400"></span>
                      Free with Ads
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      {watchProviders.ads.map(p => (
                        <ProviderBadge key={p.provider_id} provider={p} title={movie.title} fallback={watchProviders.link} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Subscription */}
                {watchProviders.flatrate.length > 0 && (
                  <div className="mb-2">
                    <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3 flex items-center gap-1">
                      <span className="inline-block w-2 h-2 rounded-full bg-gray-400"></span>
                      Subscription
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      {watchProviders.flatrate.map(p => (
                        <ProviderBadge key={p.provider_id} provider={p} title={movie.title} fallback={watchProviders.link} />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Related Movies */}
        {relatedMovies.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Movies & Shows</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {relatedMovies.map((related) => (
                <Link
                  key={related.id}
                  to={`/movie/${related.id}`}
                  onClick={() => {
                    // Store the related movie data in sessionStorage so it's available on the detail page
                    sessionStorage.setItem(`movie_${related.id}`, JSON.stringify(related));
                  }}
                  className="group block bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow overflow-hidden"
                >
                  <div className="aspect-w-2 aspect-h-3 bg-gray-200">
                    {related.poster_path ? (
                      <img
                        src={related.poster_path}
                        alt={related.title}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      className={`w-full h-48 bg-gradient-to-br from-videbits-200 to-videbits-300 ${related.poster_path ? 'hidden' : 'flex'} items-center justify-center`}
                    >
                      <Film className="w-8 h-8 text-videbits-500" />
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 text-sm mb-2 line-clamp-2 group-hover:text-videbits-600 transition-colors">
                      {related.title}
                    </h3>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      {related.category && (
                        <span className="px-2 py-1 bg-gray-100 rounded-full">
                          {related.category}
                        </span>
                      )}
                      {related.vote_average > 0 && (
                        <div className="flex items-center space-x-1">
                          <Star className="w-3 h-3 fill-videbits-400 text-videbits-400" />
                          <span>{related.vote_average.toFixed(1)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MovieDetail;
